import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Star, X, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { kind: "client", name: "", role: "", company: "", quote: "", rating: 5 };

const inputCls =
  "w-full bg-zinc-900 border border-zinc-800 px-4 py-3 text-white placeholder:text-zinc-600 text-sm focus:outline-none focus:border-primary transition-colors";

export default function TestimonialManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const api = useMemo(() => {
    const instance = axios.create({ baseURL: API });
    instance.interceptors.request.use((config) => {
      const token = localStorage.getItem("hr99_token");
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
    instance.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err.response?.status === 401) {
          localStorage.removeItem("hr99_token");
          navigate("/admin/login");
        }
        return Promise.reject(err);
      }
    );
    return instance;
  }, [navigate]);

  const load = async () => {
    try {
      const { data } = await api.get("/testimonials");
      setItems([...(data.client || []), ...(data.candidate || [])]);
    } catch {
      toast.error("Could not load testimonials");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        const { data } = await api.patch(`/testimonials/${editing}`, form);
        setItems((list) => list.map((i) => (i.id === editing ? data : i)));
        toast.success("Testimonial updated");
      } else {
        const { data } = await api.post("/testimonials", form);
        setItems((list) => [data, ...list]);
        toast.success("Testimonial added");
      }
      setForm(empty);
      setEditing(null);
      setShowForm(false);
    } catch {
      toast.error("Could not save testimonial");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (t) => {
    setForm({ kind: t.kind, name: t.name, role: t.role, company: t.company, quote: t.quote, rating: t.rating });
    setEditing(t.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    try {
      await api.delete(`/testimonials/${id}`);
      setItems((list) => list.filter((i) => i.id !== id));
      toast.success("Testimonial deleted");
    } catch {
      toast.error("Could not delete testimonial");
    }
  };

  const Group = ({ kind, title }) => {
    const list = items.filter((i) => i.kind === kind);
    return (
      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-4">{title} ({list.length})</p>
        <div className="space-y-4">
          {list.map((t) => (
            <div key={t.id} className="bg-zinc-900 border border-zinc-800 p-6" data-testid={`admin-testimonial-${t.id}`}>
              <p className="text-zinc-300 text-sm leading-relaxed">"{t.quote}"</p>
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-white">{t.name}</p>
                  <p className="text-xs text-zinc-500">{t.role} · {t.company}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="flex gap-0.5 mr-2" aria-label={`${t.rating} stars`}>
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <Star key={i} className="w-3.5 h-3.5 fill-primary text-primary" />
                    ))}
                  </span>
                  <button onClick={() => startEdit(t)} className="px-3 py-2 text-xs font-semibold border border-white/15 text-zinc-300 hover:border-primary hover:text-primary transition-colors inline-flex items-center gap-1.5" data-testid={`testimonial-edit-${t.id}`}>
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button onClick={() => remove(t.id)} className="px-3 py-2 text-xs font-semibold border border-red-500/40 text-red-400 hover:bg-red-500/10 transition-colors inline-flex items-center gap-1.5" data-testid={`testimonial-delete-${t.id}`}>
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
          {list.length === 0 && <p className="text-sm text-zinc-600">None yet — add the first one.</p>}
        </div>
      </div>
    );
  };

  return (
    <div data-testid="testimonial-manager">
      <div className="flex items-center justify-between mb-8">
        <p className="text-sm text-zinc-400">
          These appear on the website under <span className="text-white">Stories</span>. Changes go live instantly.
        </p>
        <button
          onClick={() => { setForm(empty); setEditing(null); setShowForm(!showForm); }}
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:bg-orange-400 transition-colors"
          data-testid="testimonial-add-toggle"
        >
          {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {showForm ? "Cancel" : "Add Testimonial"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-zinc-950/60 border border-white/10 p-6 md:p-8 mb-10 space-y-4" data-testid="testimonial-form">
          <p className="font-heading text-xl text-white">{editing ? "Edit testimonial" : "New testimonial"}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <select value={form.kind} onChange={set("kind")} className={`${inputCls} appearance-none`} data-testid="testimonial-kind-select">
              <option value="client" className="bg-zinc-900">Client Feedback</option>
              <option value="candidate" className="bg-zinc-900">Candidate Feedback</option>
            </select>
            <select value={form.rating} onChange={set("rating")} className={`${inputCls} appearance-none`} data-testid="testimonial-rating-select">
              {[5, 4, 3, 2, 1].map((r) => (
                <option key={r} value={r} className="bg-zinc-900">{r} star{r > 1 ? "s" : ""}</option>
              ))}
            </select>
            <input value={form.name} onChange={set("name")} placeholder="Name" className={inputCls} data-testid="testimonial-name-input" />
            <input value={form.role} onChange={set("role")} placeholder="Role (e.g. HR Head)" className={inputCls} data-testid="testimonial-role-input" />
            <input value={form.company} onChange={set("company")} placeholder="Company" className={`${inputCls} sm:col-span-2`} data-testid="testimonial-company-input" />
          </div>
          <textarea rows={4} value={form.quote} onChange={set("quote")} placeholder="Their feedback, in their words..." className={`${inputCls} resize-none`} data-testid="testimonial-quote-input" />
          <button type="submit" disabled={saving} className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 text-sm font-semibold hover:bg-orange-400 transition-colors disabled:opacity-60" data-testid="testimonial-save-button">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {editing ? "Save Changes" : "Publish Testimonial"}
          </button>
        </form>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <Group kind="client" title="Client Feedback" />
        <Group kind="candidate" title="Candidate Feedback" />
      </div>
    </div>
  );
}
